import { useParams } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import InvitBanner from "./invit-banner/InvitBanner";
import InvitKeyFacts from "./invit-key-facts/InvitKeyFacts";
import InvitFinancial from "./invit-financial/InvitFinancial";
import Operational from "./invit-operational/operational";
import Pattern from "./invit-unit-holding/unit";
import Debt from "./invit-debt-maturity/debt";
import MoreInvits from "./more-invit/more";
import "./InvitDetail.less";
import { Invit } from "../../../types/invit.types";
import service from "../../../services/service";
import { useParameters } from "../../../context/parametersContext";

const InvitsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentInvit, setCurrentInvit } = useParameters();

  // Refs for each section
  const overviewRef = useRef<HTMLDivElement | null>(null);
  const financialRef = useRef<HTMLDivElement | null>(null);
  const operationalRef = useRef<HTMLDivElement | null>(null);
  const patternRef = useRef<HTMLDivElement | null>(null);
  const debtRef = useRef<HTMLDivElement | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!id) return;
    const fetchInvit = async () => {
      const data: Invit | null = await service.getInvitById(id);
      if (data) setCurrentInvit(data);
    };
    fetchInvit();
  }, [id, setCurrentInvit]);

  const navItems = [
    { label: "Overview", ref: overviewRef },
    { label: "Financial Metrics", ref: financialRef },
    { label: "Operational Metrics", ref: operationalRef },
    { label: "Unitholding Pattern", ref: patternRef },
    { label: "Debt Maturity Profile", ref: debtRef },
  ];

  // Smooth scroll on click
  const handleScroll = (
    ref: React.RefObject<HTMLDivElement | null>,
    index: number
  ) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveIndex(index);
    }
  };

  // Observe sections as they scroll into view
  useEffect(() => {
    const sectionRefs = navItems
      .map((item) => item.ref.current)
      .filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSection = entries.find((entry) => entry.isIntersecting);
        if (visibleSection) {
          const index = sectionRefs.indexOf(
            visibleSection.target as HTMLDivElement
          );
          if (index !== -1) setActiveIndex(index);
        }
      },
      {
        root: null,
        threshold: 0.25,
      }
    );

    sectionRefs.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionRefs.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  return (
    <div className="invit-details">
      <InvitBanner invit={currentInvit} />

      {/* Navbar */}
      <nav className="invit-details__nav">
        <ul>
          {navItems.map((item, index) => (
            <li
              key={index}
              onClick={() => handleScroll(item.ref, index)}
              className={activeIndex === index ? "active" : ""}
            >
              {item.label}
            </li>
          ))}
        </ul>
      </nav>

      {/* Sections */}
      <div ref={overviewRef} style={{ scrollMarginTop: "80px" }}>
        <InvitKeyFacts invit={currentInvit} />
      </div>
      <div ref={financialRef} style={{ scrollMarginTop: "140px" }}>
        <InvitFinancial invit={currentInvit} />
      </div>
      <div ref={operationalRef} style={{ scrollMarginTop: "140px" }}>
        <Operational invit={currentInvit} />
      </div>
      <div ref={patternRef} style={{ scrollMarginTop: "140px" }}>
        <Pattern invit={currentInvit} />
      </div>
      <div ref={debtRef} style={{ scrollMarginTop: "140px" }}>
        <Debt invit={currentInvit} />
      </div>

      <MoreInvits />
    </div>
  );
};

export default InvitsDetail;
